import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { User } from '../classes/app.models';
import { CartService } from './cart.service';
import { DataService } from './data.service';
import { ExclusiveService } from './exclusive.service';
import { RecaptchaService } from './recaptcha.service';
// import { WishlistService } from './wishlist.service';

/* Outcome of an email-OTP call: `message` is the backend's own copy where it
 * sent one, so the modal can show "Incorrect code" rather than a generic error. */
export interface OtpResult {
    success: boolean;
    message: string;
}


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    public user;

    /**
     * Why the last sign-in or sign-up failed, when the backend said something
     * worth repeating.
     *
     * login() and register() answer true/false, which used to be enough because
     * a wrong password was the only way they could fail. A rejected reCAPTCHA is
     * a second way, and "Incorrect login id/pass" is the wrong thing to tell
     * someone whose password was never even checked — so the backend's own copy
     * is parked here for the component to show instead. Cleared at the start of
     * every attempt.
     */
    public lastAuthMessage = '';

    constructor(public http: HttpClient,
        public appService: ApiService,
        public dataService: DataService,
        public exclusiveService: ExclusiveService,
        private recaptcha: RecaptchaService
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        if (localStorage.getItem('currentUser')) {
            return this.currentUserSubject.value;
        } else {
            this.currentUserSubject.next(null);
            return null;
        }
    }

    public setCurrentUser(user: User): void {
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    public get getAccessToken(): string {
        if (localStorage.getItem('currentUser')) {
            return this.currentUserSubject.value.token;
        } else {
            this.currentUserSubject.next(null);
            return null;
        }
    }

    /**
     * Password sign-in.
     *
     * A reCAPTCHA v3 token is fetched first and sent with the credentials; the
     * backend verifies it before it looks at them. The token is per-submission —
     * single-use and short-lived — which is why it is taken here rather than
     * once when the form opens.
     *
     * The error arm is not optional. /login answers 200 even for a bad password,
     * so this call had no failure path at all; the captcha middleware answers
     * 422, and without an arm to catch it the promise would never settle and the
     * modal would sit on its spinner forever.
     */
    public login(username, password): Promise<boolean> {
        return new Promise(resolve => {
            this.lastAuthMessage = '';

            this.recaptcha.execute(RecaptchaService.LOGIN).then(token => {
                this.appService.signIn(username, password, token).subscribe(
                    data => {
                        this.establishSession(data).then(resolve);
                    },
                    error => {
                        this.lastAuthMessage = this.requestError(error);
                        resolve(false);
                    }
                );
            });
        });
    }

    /*
     * Email OTP -- step 1. Mails a code; no session is created yet.
     *
     * Captcha-checked like the password forms: pressing "Continue" on the login
     * or register form is the same submission whichever mode it is in, and this
     * is the one that costs an outbound email.
     */
    public sendLoginEmailOtp(email): Promise<OtpResult> {
        return this.recaptcha.execute(RecaptchaService.LOGIN)
            .then(token => this.otpRequest(this.appService.sendLoginEmailOtp(email, token)));
    }

    public sendRegisterEmailOtp(email): Promise<OtpResult> {
        return this.recaptcha.execute(RecaptchaService.REGISTER)
            .then(token => this.otpRequest(this.appService.sendRegisterEmailOtp(email, token)));
    }

    /*
     * Email OTP -- step 2. The backend answers these with the same payload as
     * signIn(), so a verified code lands the customer in a full session right
     * here. The register variant creates the account before doing so, which is
     * why it needs no follow-up call to log in.
     */
    public verifyLoginEmailOtp(email, otp): Promise<OtpResult> {
        return this.otpSession(this.appService.verifyLoginEmailOtp(email, otp));
    }

    public verifyRegisterEmailOtp(email, otp): Promise<OtpResult> {
        return this.otpSession(this.appService.verifyRegisterEmailOtp(email, otp));
    }

    /**
     * Sign in from a payload the backend handed back through a browser redirect
     * (Google). Unlike the XHR flows there is no response body to read, so the
     * callback route decodes the payload out of the query string and passes it
     * here -- from that point it is the same session as any other.
     *
     * `cart` is absent from a redirect payload; the caller refreshes it.
     */
    public applyRedirectSession(payload): Promise<boolean> {
        if (!payload || !payload.user || payload.user.msg !== 'success' || !payload.user.data) {
            return Promise.resolve(false);
        }

        return this.establishSession(payload);
    }

    /* Send-OTP calls: nothing to store, just relay the outcome. */
    private otpRequest(request: Observable<any>): Promise<OtpResult> {
        return new Promise(resolve => {
            request.subscribe(
                data => resolve({
                    success: !!(data && data.success),
                    message: this.otpMessage(data, 'Could not send the code. Please try again.')
                }),
                error => resolve({ success: false, message: this.requestError(error) })
            );
        });
    }

    /* Verify calls: a success payload is a sign-in payload. */
    private otpSession(request: Observable<any>): Promise<OtpResult> {
        return new Promise(resolve => {
            request.subscribe(
                data => {
                    if (data && data.success && data.user && data.user.msg === 'success') {
                        this.establishSession(data).then(stored => resolve({
                            success: stored,
                            message: stored
                                ? this.otpMessage(data, 'Signed in.')
                                : 'Could not sign you in just now. Please try again.'
                        }));
                    } else {
                        resolve({
                            success: false,
                            message: this.otpMessage(data, 'Could not verify that code. Please try again.')
                        });
                    }
                },
                error => resolve({ success: false, message: this.requestError(error) })
            );
        });
    }

    private otpMessage(data, fallback: string): string {
        return (data && data.message) ? data.message : fallback;
    }

    /*
     * Failures arrive as non-2xx carrying {success, statusCode, message}. The
     * throttle middleware is the exception -- it answers 429 from outside the
     * controller, so there is no message of ours to read.
     *
     * Shared with the password flows: the captcha middleware rejects in the same
     * shape, so one reader serves both.
     */
    private requestError(error): string {
        if (error && error.error && error.error.message) {
            return error.error.message;
        }
        if (error && error.status === 429) {
            return 'Too many attempts. Please wait a minute and try again.';
        }
        return 'Something went wrong. Please try again.';
    }

    /**
     * Turn a sign-in payload into a stored session.
     *
     * Shared by password sign-in and by both email-OTP verify endpoints, which
     * the backend deliberately answers in the same shape -- so from here on an
     * OTP session is indistinguishable from a password one.
     */
    private establishSession(data): Promise<boolean> {
        return new Promise(resolve => {
                this.user = data.user;
                if (this.user.msg === 'success' && !(data.hasOwnProperty('isAdmin'))) {
                    this.clearAll();
                    this.user.data.customers_password1 = undefined;
                    this.user.data.isExclusive = false;
                    if(this.user && this.user.data && this.user.data.customer_account_reg_from == 'ibudgetphoto.com'){
                        // budget uer login
                        this.user.data.user_type = 'BUDGET';
                        localStorage.setItem('currentUser', JSON.stringify(this.user.data));
                        this.currentUserSubject.next(this.user.data);
                        this.dataService.setData(this.user.data);
                    }else{
                        // indiapicture user login
                        this.user.data.user_type = 'INDIAPICTURE';
                        const applyUserPermission = () => {
                        // Session first -- see persistUser().
                        this.persistUser();

                        this.exclusiveService.getUserPermission(this.user.data.customers_id).subscribe((permissionData: any) => {
                            this.user.data.download_collections = permissionData?.data?.collection;
                            this.user.data.comp_collection = permissionData?.data?.comp_collection;
                            this.user.data.search_collection = permissionData?.data?.search_collection;
                            this.user.data.user_size = permissionData?.data?.user_size;
                            this.persistUser();
                            resolve(true);
                        }, () => {
                            /*
                             * Entitlements are an enhancement on the session, not the
                             * session itself. This used to have no error path at all,
                             * so a failed lookup meant resolve() never fired and the
                             * sign-in promise hung forever with no message.
                             */
                            resolve(true);
                        });
                    };

                    const currentUserPlan = this.user?.data?.plan_id;
                    if (currentUserPlan && currentUserPlan != 0) {
                        this.appService.getPlan(currentUserPlan).subscribe(planData => {
                            // console.log('planData => ', planData);
                            this.user.data.plan_collections = planData?.data?.collection_id;
                            applyUserPermission();
                        }, () => {
                            /*
                             * Same reasoning as the getUserPermission arm below,
                             * which this one was missing: the plan's collections
                             * are an enhancement on the session, not the session
                             * itself. Without this, a failed plan lookup skipped
                             * applyUserPermission() entirely -- so persistUser()
                             * never ran, no session was stored, and the promise
                             * never resolved. The caller sat on a spinner while
                             * the previous localStorage session stayed in place,
                             * which reads as "signed in but the header still
                             * shows Sign Up".
                             */
                            applyUserPermission();
                        });
                    } else {
                        applyUserPermission();
                    }

                    }

                    if (data.cart != null) {
                        this.appService.Data.cartList = data.cart.imagesArray;
                        this.appService.Data.totalCartCount = data.cart.imagesArray.length;

                        data.cart.imagesArray.forEach(product => {
                            this.appService.Data.totalPrice += +product.price;

                        });
                    }
                } else if (this.user.msg === 'success' && data.hasOwnProperty('isAdmin')) {
                        this.clearAll();
                        this.user.data.customers_password1 = undefined;
                        this.user.data.isExclusive = true;
                        this.user.data.user_type = 'EXCLUSIVE';
                        if(data.isAdmin['data']){
                            this.user.data.isAdmin = false;
                        }else{
                            this.user.data.isAdmin = true;
                        }
                    
                        // Session first -- see persistUser().
                        this.persistUser();

                        this.exclusiveService.getUserPermission(this.user.data.customers_id).subscribe((planData:any) => {
                            this.user.data.download_collections = planData?.data[0]?.collection;
                            this.user.data.comp_collection = planData?.data[0]?.comp_collection;
                            this.user.data.search_collection = planData?.data[0]?.search_collection;
                            this.user.data.user_size = planData?.data[0]?.user_size;
                            this.persistUser();
                            resolve(true);
                        }, () => {
                            // See the INDIAPICTURE branch: a failed lookup must not
                            // hang the sign-in promise.
                            resolve(true);
                        });

                        if (data.cart != null) {
                            this.appService.Data.cartList = data.cart.imagesArray;
                            this.appService.Data.totalCartCount = data.cart.imagesArray.length;

                            data.cart.imagesArray.forEach(product => {
                                this.appService.Data.totalPrice += +product.price;
                            });
                        }
                } else {
                    this.clearAll();
                    resolve(false);
                }
        });
    }

    public exclusiveLogin(username, password): Promise<boolean> {
        //  console.log('exclusiveLogin ', username, password)
        return new Promise(resolve => {
            this.appService.exclusiveSignIn(username, password).subscribe(data => {
                //  console.log('exclusiveSignIn => ', data)
                this.user = data.user;
                if (this.user.msg === 'success') {
                    this.clearAll();
                    this.user.data.customers_password1 = undefined;
                    this.user.data.isExclusive = true;
                    if(data.isAdmin['data']){
                        this.user.data.isAdmin = false;
                    }else{
                        this.user.data.isAdmin = true;
                    }
                    // localStorage.setItem('currentUser', JSON.stringify(this.user.data));
                    // this.currentUserSubject.next(this.user.data);

                    // const currentUserPlan = this.user?.data?.plan_id;
                    // if (currentUserPlan && currentUserPlan != 0) {
                        // Session first -- see persistUser().
                        this.persistUser();

                        this.exclusiveService.getUserPermission(this.user.data.customers_id).subscribe((planData:any) => {
                            this.user.data.download_collections = planData?.data[0]?.collection;
                            this.user.data.comp_collection = planData?.data[0]?.comp_collection;
                            this.user.data.search_collection = planData?.data[0]?.search_collection;
                            this.user.data.user_size = planData?.data[0]?.user_size;
                            this.persistUser();
                        }, () => {
                            // Session already stored above; entitlements just stay empty.
                        });
                    // }
                    //  else {
                    //     localStorage.setItem('currentUser', JSON.stringify(this.user.data));
                    //     this.currentUserSubject.next(this.user.data);
                    //     this.dataService.setData(this.user.data);

                    // }

                    if (data.cart != null) {
                        this.appService.Data.cartList = data.cart.imagesArray;
                        this.appService.Data.totalCartCount = data.cart.imagesArray.length;

                        data.cart.imagesArray.forEach(product => {
                            this.appService.Data.totalPrice += +product.price;

                        });
                    }
                    resolve(true);
                } else {
                    this.clearAll();
                    resolve(false);
                }
            });
        });
    }

    /**
     * Sign-up. Captcha-checked exactly as login() is, and for the same reason:
     * this endpoint creates an account and sends mail, so it is worth a bot's
     * while to hammer. See login() on why the error arm has to be there.
     */
    public register(name, email, phone, password): Promise<boolean> {
        return new Promise(resolve => {
            this.lastAuthMessage = '';

            this.recaptcha.execute(RecaptchaService.REGISTER).then(token => {
                this.appService.register(name, email, phone, password, token).subscribe(
                    data => {
                        this.clearAll();
                        if (data.statusCode === 200) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    },
                    error => {
                        this.lastAuthMessage = this.requestError(error);
                        resolve(false);
                    }
                );
            });
        });
    }

    public updateProfile(firstname, lastname, email, company, com_type, job_desc, address, state, pincode, city, phone, fax): Promise<boolean> {
        return new Promise(resolve => {
            const currentUserActive = this.currentUserValue;
            this.appService.updateProfile(firstname, lastname, email, company, com_type, job_desc, address, state, pincode, city
                , phone, fax, currentUserActive.customers_id).subscribe(data => {
                        /*
                         * Merged onto the stored user, not swapped for the API
                         * response.
                         *
                         * login() derives fields the profile endpoints know
                         * nothing about -- user_type, isExclusive, the token, and
                         * the collection/size grants from getUserPermission. This
                         * used to assign `data` wholesale and then re-apply only
                         * isExclusive and the token, so user_type was silently
                         * dropped.
                         *
                         * Losing it blanks pages: the media detail template picks
                         * its layout with `user_type == 'INDIAPICTURE'` /
                         * 'BUDGET' / isExclusive, so an undefined user_type
                         * matched none of the three and the whole <main> -- image
                         * detail section included -- rendered nothing until the
                         * customer signed in again.
                         */
                    this.user = Object.assign({}, currentUserActive, data);
                    if (data) {
                        this.user.customers_password1 = undefined;
                        this.user.token = currentUserActive.token;
                        this.user.isExclusive = (currentUserActive as any)?.isExclusive === true;
                        this.user.user_type = (currentUserActive as any)?.user_type;
                        localStorage.setItem('currentUser', JSON.stringify(this.user));
                        this.currentUserSubject.next(this.user);
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                });
        });
    }

    public getUserDetail(): Promise<boolean> {
        return new Promise(resolve => {
            const currentUserActive = this.currentUserValue;
            this.appService.getUserDetail(currentUserActive.customers_email_address).subscribe(data => {
                // Same merge as updateProfile(); see the comment there.
                this.user = Object.assign({}, currentUserActive, data);
                if (data) {
                    this.user.customers_password1 = undefined;
                    this.user.token = currentUserActive.token;
                    this.user.isExclusive = (currentUserActive as any)?.isExclusive === true;
                    this.user.user_type = (currentUserActive as any)?.user_type;
                    localStorage.setItem('currentUser', JSON.stringify(this.user));
                    this.currentUserSubject.next(this.user);
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    public changePassword(currentPassword, newPassword): Promise<boolean> {
        return new Promise(resolve => {
            const currentUserActive: any = this.currentUserValue;
            this.appService.changePassword(currentUserActive.customers_email_address, newPassword, currentPassword, currentUserActive.customers_password, false).subscribe(data => {

                if (data.statusCode === 200) {
                    currentUserActive.customers_password = data.pass;
                    // this.user.token = currentUserActive.token;
                    /*
                     * isExclusive is LEFT ALONE. It used to be set to false here
                     * unconditionally, which demoted an exclusive account the
                     * moment it changed its password: the header then read the
                     * balance from the non-exclusive endpoint, which knows
                     * nothing about the exclusive plan tables, and the credit
                     * pill went blank until the next sign-in.
                     *
                     * Changing a password says nothing about what kind of
                     * account it is.
                     */
                    localStorage.setItem('currentUser', JSON.stringify(currentUserActive));
                    this.currentUserSubject.next(currentUserActive);
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    public clearAll(): void {

        this.appService.Data.cartList = [];
        this.appService.Data.totalPrice = 0;
        this.appService.Data.totalCartCount = 0;
        // this.wishlistService.clearAllWishlist();
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
        this.dataService.setData(null);


        // this.cartService.cartStream.next([]);
        // this.cartService.qtyTotal.next(0);
        // this.cartService.priceTotal.next(0);
        // this.cartService.cartItems = [];
    }

    logout(): void {
        // this.appService.logout().subscribe(data => {
        // remove user from local storage and set current user to null
        this.clearAll();
        // });
    }

    /**
     * Store the signed-in user and publish it.
     *
     * Pulled out because the session has to exist BEFORE the entitlement lookup,
     * not after it. /user-permission now requires the bearer token, and
     * JwtInterceptor reads that token from the stored user -- so a login that
     * only stored the user inside the lookup's own callback could never
     * authenticate the lookup.
     */
    private persistUser(): void {
        localStorage.setItem('currentUser', JSON.stringify(this.user.data));
        this.currentUserSubject.next(this.user.data);
        this.dataService.setData(this.user.data);
    }

}
